import { Component } from 'react';
import { Button } from '../common/button';
import { QrCodeModalBloc, QrCodeComponent } from '../common/qr_code_modal';
import { BlocProvider, AppDep, StreamBuilder } from '../../provider';
import Log from '../../common/util/log';
import autoBind from 'auto-bind';
import { BehaviorSubject } from 'rxjs';
import { GroupInfo } from '../../controller/do';
import { GlobalContext } from '../../common/modal_boundary';
import { MDBTable, MDBTableBody, MDBTableHead } from 'mdbreact';
import ModalComponentV2 from '../common/ModalComponentV2';
import { ProcessingModalLayout, StatusModalLayout } from '../common/ProcessingModal';
import { Backend } from '../../controller/backend';

const TAG = 'DownloadWhatsappContacts';

class Bloc {
	public qrCodeBloc: QrCodeModalBloc;
	public readonly whatsappGroups: BehaviorSubject<GroupInfo[]> = new BehaviorSubject(
		[] as GroupInfo[]
	) as BehaviorSubject<GroupInfo[]>;
	public readonly importModalVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public readonly importStatus: BehaviorSubject<{ message: string; complete: boolean } | undefined> =
		new BehaviorSubject<
			| {
					message: string;
					complete: boolean;
			  }
			| undefined
		>(undefined);
	public readonly selectedGroup: BehaviorSubject<GroupInfo | null> = new BehaviorSubject<GroupInfo | null>(null);
	private _api: Backend.Api;
	private _currentSessionId: number = 0;

	constructor(appDep: AppDep) {
		autoBind(this);
		this.qrCodeBloc = new QrCodeModalBloc(appDep.api);
		this._api = appDep.api;
	}

	// Generate a unique session ID using random number + timestamp
	private generateSessionId(): number {
		const randomPart = Math.floor(Math.random() * 100000);
		const timePart = Date.now() % 1000000;
		return randomPart * 1000000 + timePart;
	}

	destroy() {
		// Cleanup if needed
	}

	@GlobalContext.handlErrorAsModal()
	async handleShowQr() {
		Log.d(TAG, 'inside handleShowQr');
		try {
			const sessionId = await this.getSessionId();
			await Promise.all([
				this.qrCodeBloc.doQrCodeValidation(sessionId),
				this.fetchWhatsappGroups().finally(() => this.qrCodeBloc.onModalClose()),
			]);
		} catch (error) {
			Log.e(TAG, 'Error showing QR code:', error);
			throw error;
		}
	}

	private async getSessionId(): Promise<number> {
		this._currentSessionId = this.generateSessionId();
		Log.d(TAG, 'Generated new session ID:', this._currentSessionId);
		return this._currentSessionId;
	}

	@GlobalContext.handlErrorAsModal()
	async fetchWhatsappGroups() {
		Log.d(TAG, 'inside fetchWhatsappGroups');
		if (this._currentSessionId === 0) {
			this._currentSessionId = this.generateSessionId();
		}
		Log.d(TAG, 'Using session ID:', this._currentSessionId);
		const groups = await this._api.getWhatsappGroup(this._currentSessionId);
		this.whatsappGroups.next(groups);
	}

	@GlobalContext.handlErrorAsModal()
	async importContacts(group: GroupInfo) {
		Log.d(TAG, 'inside importContacts', group);
		this.selectedGroup.next(group);
		this.importModalVisible.next(true);
		this.importStatus.next({ message: 'Importing contacts', complete: false });

		try {
			const success = await this._api.importWhatsappGroupContacts(group.jid);

			if (success) {
				this.importStatus.next({
					message: `Successfully imported ${group.member_count} contacts from ${group.name}`,
					complete: true,
				});
			} else {
				this.importStatus.next({
					message: `Failed to import contacts from ${group.name}`,
					complete: true,
				});
			}
		} catch (error) {
			Log.e(TAG, 'Error importing contacts:', error);
			this.importStatus.next({
				message: `Error importing contacts: ${error}`,
				complete: true,
			});
		}
	}

	closeImportModal() {
		this.importModalVisible.next(false);
		this.importStatus.next(undefined);
		this.selectedGroup.next(null);
	}
}

export class DownloadWhatsappContacts extends Component<any, {}> {
	render() {
		return (
			<BlocProvider
				create={(appDep: AppDep) => new Bloc(appDep)}
				destroy={(bloc: Bloc) => bloc.destroy()}
				builder={(bloc: Bloc) => (
					<div className="m-2">
						<div className="text-center mb-4">
							<h4>WhatsApp Groups</h4>
							<p className="text-muted">Connect with WhatsApp to download contacts from a group</p>
						</div>

						<StreamBuilder
							stream={bloc.whatsappGroups}
							builder={(groups: GroupInfo[]) => {
								return (
									<div>
										{groups.length > 0 ? (
											<div className="mt-4">
												<MDBTable bordered hover responsive>
													<MDBTableHead>
														<tr>
															<th>Name</th>
															<th>Topic</th>
															<th>Members</th>
															<th>Action</th>
														</tr>
													</MDBTableHead>
													<MDBTableBody>
														{groups.map((group, index) => (
															<tr key={index}>
																<td>{group.name}</td>
																<td>{group.topic}</td>
																<td>{group.member_count}</td>
																<td>
																	<Button
																		focused
																		className="btn button btn-sm"
																		onClick={() => bloc.importContacts(group)}
																	>
																		Import
																	</Button>
																</td>
															</tr>
														))}
													</MDBTableBody>
												</MDBTable>
											</div>
										) : (
											<div className="text-center mt-4">
												<Button focused className="btn button" onClick={bloc.handleShowQr}>
													Connect to WhatsApp
												</Button>
											</div>
										)}
									</div>
								);
							}}
						/>

						<QrCodeComponent bloc={bloc.qrCodeBloc} />

						<StreamBuilder
							stream={bloc.importModalVisible}
							builder={(visible: boolean) => (
								<StreamBuilder
									stream={bloc.importStatus}
									builder={(status: { message: string; complete: boolean } | undefined) =>
										status ? (
											<ModalComponentV2
												isOpen={visible}
												toggle={bloc.closeImportModal}
												body={<ProcessingModalLayout message={status.message} />}
												buttonList={
													status.complete
														? [
																<Button
																	focused
																	className="btn button"
																	onClick={bloc.closeImportModal}
																>
																	OK
																</Button>,
														  ]
														: []
												}
											/>
										) : (
											<></>
										)
									}
								/>
							)}
						/>
					</div>
				)}
			/>
		);
	}
}
